import { ApiResponse } from './types';
import { apiClient } from './core/apiClient';
import {
  ISubscription,
  ISubscriptionPlan,
} from '../types/interfaces/ISubscription';
import {
  IPaymentMethod,
  IPayment,
  ICreditCard,
  ICreditCardFormData,
  ICardValidationResponse,
} from '../types/interfaces/IPaymentMethod';

// ── Plans ────────────────────────────────────────────────────────────────────

export async function getAvailablePlans(): Promise<ApiResponse<ISubscriptionPlan[]>> {
  return apiClient.get<ISubscriptionPlan[]>('/subscriptions/plans');
}

export async function getPlanById(id: string): Promise<ApiResponse<ISubscriptionPlan>> {
  return apiClient.get<ISubscriptionPlan>(`/subscriptions/plans/${id}`);
}

export async function createPlan(
  plan: Omit<ISubscriptionPlan, 'id'>
): Promise<ApiResponse<ISubscriptionPlan>> {
  return apiClient.post<ISubscriptionPlan>('/subscriptions/plans', plan);
}

export async function updatePlan(
  id: string,
  plan: Partial<ISubscriptionPlan>
): Promise<ApiResponse<ISubscriptionPlan>> {
  return apiClient.patch<ISubscriptionPlan>(`/subscriptions/plans/${id}`, plan);
}

export async function deletePlan(id: string): Promise<ApiResponse<boolean>> {
  return apiClient.delete<boolean>(`/subscriptions/plans/${id}`);
}

export async function getPlansByCategory(
  category: string
): Promise<ApiResponse<ISubscriptionPlan[]>> {
  return apiClient.get<ISubscriptionPlan[]>(
    `/subscriptions/plans?category=${encodeURIComponent(category)}`
  );
}

// ── Subscriptions ────────────────────────────────────────────────────────────

export async function getCurrentSubscription(
  userId: string
): Promise<ApiResponse<ISubscription | null>> {
  return apiClient.get<ISubscription | null>(`/subscriptions/current?userId=${userId}`);
}

export async function createSubscription(
  userId: string,
  subscriptionData: any
): Promise<ApiResponse<ISubscription>> {
  return apiClient.post<ISubscription>('/subscriptions', { userId, ...subscriptionData });
}

export async function cancelSubscription(
  subscriptionId: string
): Promise<ApiResponse<boolean>> {
  return apiClient.patch<boolean>(`/subscriptions/${subscriptionId}/cancel`);
}

export async function reactivateSubscription(
  subscriptionId: string
): Promise<ApiResponse<boolean>> {
  return apiClient.patch<boolean>(`/subscriptions/${subscriptionId}/reactivate`);
}

export async function changePlan(
  subscriptionId: string,
  planId: string
): Promise<ApiResponse<ISubscription>> {
  return apiClient.patch<ISubscription>(`/subscriptions/${subscriptionId}/change-plan`, {
    planId,
  });
}

// ── Payment Methods ──────────────────────────────────────────────────────────

export async function getPaymentMethods(): Promise<ApiResponse<IPaymentMethod[]>> {
  return apiClient.get<IPaymentMethod[]>('/subscriptions/payment-methods');
}

export async function getPaymentMethodById(
  id: string
): Promise<ApiResponse<IPaymentMethod>> {
  return apiClient.get<IPaymentMethod>(`/subscriptions/payment-methods/${id}`);
}

export async function createPaymentMethod(
  data: Omit<IPaymentMethod, 'id'>
): Promise<ApiResponse<IPaymentMethod>> {
  return apiClient.post<IPaymentMethod>('/subscriptions/payment-methods', data);
}

export async function updatePaymentMethod(
  id: string,
  data: Partial<IPaymentMethod>
): Promise<ApiResponse<IPaymentMethod>> {
  return apiClient.patch<IPaymentMethod>(`/subscriptions/payment-methods/${id}`, data);
}

export async function deletePaymentMethod(id: string): Promise<ApiResponse<boolean>> {
  return apiClient.delete<boolean>(`/subscriptions/payment-methods/${id}`);
}

export async function setDefaultPaymentMethod(
  id: string
): Promise<ApiResponse<IPaymentMethod>> {
  return apiClient.patch<IPaymentMethod>(
    `/subscriptions/payment-methods/${id}/set-default`
  );
}

export async function getPaymentHistory(
  userId: string
): Promise<ApiResponse<IPayment[]>> {
  return apiClient.get<IPayment[]>(`/subscriptions/payments?userId=${userId}`);
}

export async function processPayment(
  subscriptionId: string,
  paymentData: any
): Promise<ApiResponse<IPayment>> {
  return apiClient.post<IPayment>(
    `/subscriptions/${subscriptionId}/payments`,
    paymentData
  );
}

// ── Credit Cards ─────────────────────────────────────────────────────────────

export async function getCreditCards(userId: string): Promise<ApiResponse<ICreditCard[]>> {
  return apiClient.get<ICreditCard[]>(`/users/${userId}/credit-cards`);
}

export async function getCreditCardById(id: string): Promise<ApiResponse<ICreditCard>> {
  return apiClient.get<ICreditCard>(`/credit-cards/${id}`);
}

export async function createCreditCard(
  userId: string,
  cardData: ICreditCardFormData
): Promise<ApiResponse<ICreditCard>> {
  return apiClient.post<ICreditCard>(`/users/${userId}/credit-cards`, cardData);
}

export async function updateCreditCard(
  id: string,
  cardData: Partial<ICreditCard>
): Promise<ApiResponse<ICreditCard>> {
  return apiClient.patch<ICreditCard>(`/credit-cards/${id}`, cardData);
}

export async function deleteCreditCard(id: string): Promise<ApiResponse<boolean>> {
  return apiClient.delete<boolean>(`/credit-cards/${id}`);
}

export async function setDefaultCreditCard(id: string): Promise<ApiResponse<ICreditCard>> {
  return apiClient.patch<ICreditCard>(`/credit-cards/${id}/set-default`);
}

export async function validateCreditCard(
  cardData: ICreditCardFormData
): Promise<ApiResponse<ICardValidationResponse>> {
  return apiClient.post<ICardValidationResponse>('/credit-cards/validate', cardData);
}
